import { forwardRef, InputHTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  hasError?: boolean;
}

const Wrapper = styled.div<{ $hasError?: boolean }>`
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid
    ${({ theme, $hasError }) =>
      $hasError ? theme.colors.danger : theme.colors.border};
  transition: all ${({ theme }) => theme.transitions.base};
  gap: 8px;

  &:hover {
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.danger : theme.colors.borderStrong};
  }

  &:focus-within {
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.danger : theme.colors.primary};
    box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.borderFocus};
  }

  > svg {
    color: ${({ theme }) => theme.colors.textMuted};
    flex-shrink: 0;
  }
`;

const Field = styled.input`
  flex: 1;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textPrimary};

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ startIcon, endIcon, hasError, className, ...rest }, ref) => (
    <Wrapper $hasError={hasError} className={className}>
      {startIcon}
      <Field ref={ref} aria-invalid={hasError || undefined} {...rest} />
      {endIcon}
    </Wrapper>
  ),
);

Input.displayName = 'Input';
